/**
 * Token Gate Landing Page
 * CODITECT-style authentication page
 */

import { useState, type FormEvent } from 'react';
import { Eye, EyeOff, AlertCircle, Moon, Sun } from 'lucide-react';
import { useAuth } from '@/lib/auth';

export function TokenGatePage(): React.ReactElement {
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const { login } = useAuth();

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    setTimeout(() => {
      const success = login(token.trim());
      if (!success) {
        setError('Invalid access token');
        setToken('');
      }
      setIsLoading(false);
    }, 300);
  };

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${isDark ? 'bg-slate-900' : 'bg-[#f8f9fa]'}`}>
      {/* Header */}
      <header className={`border-b transition-colors duration-300 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between px-8 py-4">
          <div className="flex items-center gap-3">
            {/* CODITECT Logo */}
            <img
              src="/coditect-logo.png"
              alt="CODITECT"
              className="h-9 w-auto"
            />
            <div className="flex flex-col">
              <span className={`text-[15px] font-bold tracking-tight leading-tight transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>
                SECONDARY MARKET
              </span>
              <span className="text-[11px] font-semibold text-indigo-500 tracking-[0.15em] leading-tight">
                LP INVESTMENT PLATFORM
              </span>
            </div>
          </div>

          {/* Dark Mode Toggle */}
          <button
            onClick={() => setIsDark(!isDark)}
            className={`px-4 py-1.5 border rounded text-sm font-medium transition-colors flex items-center gap-2 ${
              isDark
                ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {isDark ? 'Light' : 'Dark'}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-[420px]">
          {/* Card */}
          <div className={`rounded-2xl shadow-xl px-10 py-10 transition-colors duration-300 ${
            isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-100'
          }`}>
            {/* Restricted Access Label */}
            <p className={`text-[11px] font-semibold tracking-[0.2em] mb-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              RESTRICTED ACCESS
            </p>

            <h2 className={`text-[26px] font-bold mb-2 leading-tight transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Secondary Market Fund
            </h2>
            <p className={`text-[15px] mb-8 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              Enter your access token to continue.
            </p>

            <form onSubmit={handleSubmit}>
              {/* Access Token Label */}
              <label className={`block text-[11px] font-bold tracking-[0.1em] mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                ACCESS TOKEN
              </label>

              {/* Token Input with Show/Hide */}
              <div className="relative mb-5">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className={`w-full px-4 py-3.5 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-all text-[15px] ${
                    isDark
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                  autoFocus
                  autoComplete="off"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors p-1 ${
                    isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'
                  }`}
                  tabIndex={-1}
                  title={showToken ? 'Hide token' : 'Show token'}
                >
                  {showToken ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
                  isDark ? 'bg-red-900/30 border border-red-800 text-red-400' : 'bg-red-50 border border-red-200 text-red-600'
                }`}>
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!token.trim() || isLoading}
                className={`w-full py-3.5 px-4 font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center text-[15px] ${
                  isDark
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500 focus:ring-offset-slate-800'
                    : 'bg-[#334155] text-white hover:bg-[#1e293b] focus:ring-slate-400'
                }`}
              >
                {isLoading ? (
                  <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Continue'
                )}
              </button>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className={`border-t transition-colors duration-300 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between px-8 py-4 text-[12px]">
          <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>
            &copy; 2026 AZ1.AI Inc. All rights reserved
          </span>
          <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>
            Created by Matias Meirelles
          </span>
          <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>
            Confidential — Authorized Use Only
          </span>
        </div>
      </footer>
    </div>
  );
}
